/**
 * CardSprite - カードスプライト
 * 個々のカードの表示、アニメーション、インタラクションを担当
 */
import Phaser from 'phaser';
import { Card } from '../../core/Card';
import { CardColor, CardType } from '../../types/card';
import { PHASER_CONFIG } from '../../utils/constants';

export class CardSprite extends Phaser.GameObjects.Container {
  private cardImage!: Phaser.GameObjects.Image;
  private glowEffect!: Phaser.GameObjects.Graphics;
  private card: Card;
  private isFaceDown: boolean;
  private isPlayable: boolean = false;
  private isSelected: boolean = false;
  private hoverPreviewEnter: ((card: Card) => void) | null = null;
  private hoverPreviewLeave: ((card: Card) => void) | null = null;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    card: Card,
    faceDown: boolean = false
  ) {
    super(scene, x, y);

    this.card = card;
    this.isFaceDown = faceDown;

    this.createSprite();
    this.createGlowEffect();
    this.setupInteractive();
  }

  /**
   * カードスプライトを作成
   */
  private createSprite(): void {
    const textureKey = this.isFaceDown ? 'card_back' : this.getTextureKey();

    this.cardImage = this.scene.add.image(0, 0, textureKey);
    this.cardImage.setDisplaySize(
      PHASER_CONFIG.CARD_SIZE.width,
      PHASER_CONFIG.CARD_SIZE.height
    );

    this.add(this.cardImage);
  }

  /**
   * 光るエフェクトを作成（プレイ可能時）
   */
  private createGlowEffect(): void {
    this.glowEffect = this.scene.add.graphics();
    this.glowEffect.setVisible(false);
    this.add(this.glowEffect);

    // 光るエフェクトを描画
    this.updateGlowEffect();
  }

  /**
   * 光るエフェクトを更新
   */
  private updateGlowEffect(): void {
    this.glowEffect.clear();

    const w = PHASER_CONFIG.CARD_SIZE.width;
    const h = PHASER_CONFIG.CARD_SIZE.height;

    // 外側の光
    this.glowEffect.lineStyle(6, 0xffff00, 0.3);
    this.glowEffect.strokeRoundedRect(-w / 2 - 6, -h / 2 - 6, w + 12, h + 12, 14);

    this.glowEffect.lineStyle(4, 0xffff00, 0.5);
    this.glowEffect.strokeRoundedRect(-w / 2 - 3, -h / 2 - 3, w + 6, h + 6, 13);

    this.glowEffect.lineStyle(2, 0xffff00, 0.8);
    this.glowEffect.strokeRoundedRect(-w / 2, -h / 2, w, h, 12);
  }

  /**
   * インタラクティブ設定
   */
  private setupInteractive(): void {
    const hitArea = new Phaser.Geom.Rectangle(
      -PHASER_CONFIG.CARD_SIZE.width / 2,
      -PHASER_CONFIG.CARD_SIZE.height / 2,
      PHASER_CONFIG.CARD_SIZE.width,
      PHASER_CONFIG.CARD_SIZE.height
    );

    this.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    // ホバーエフェクト
    this.on('pointerover', () => {
      if (this.isPlayable && !this.isFaceDown) {
        this.scene.tweens.add({
          targets: this,
          y: this.y - 15,
          scaleX: 1.1,
          scaleY: 1.1,
          duration: 150,
          ease: 'Power2',
        });
        // 「もし出したら」プレビュー（プレイ可能な自分の手札カードのみ）
        this.hoverPreviewEnter?.(this.card);
      }
    });

    this.on('pointerout', () => {
      if (!this.isSelected) {
        this.scene.tweens.add({
          targets: this,
          scaleX: 1,
          scaleY: 1,
          duration: 150,
          ease: 'Power2',
        });
      }
      // ホバーが外れた瞬間に常にプレビュー解除（プレイ可否の状態切替に追従）
      this.hoverPreviewLeave?.(this.card);
    });
  }

  /**
   * ホバープレビュー用のコールバックを登録
   * カードがプレイ可能で表向きの時のみ enter が発火する。
   * @param onEnter pointerover で呼ばれる（プレイ可能&表向き時のみ）
   * @param onLeave pointerout で呼ばれる（常時）
   */
  public setHoverPreviewCallbacks(
    onEnter: ((card: Card) => void) | null,
    onLeave: ((card: Card) => void) | null
  ): void {
    this.hoverPreviewEnter = onEnter;
    this.hoverPreviewLeave = onLeave;
  }

  /**
   * カードのテクスチャキーを取得
   */
  private getTextureKey(): string {
    const color = this.card.color;
    const type = this.card.type;
    const value = this.card.value;

    // ワイルドカード
    if (type === CardType.WILD) {
      return 'card_wild';
    }
    if (type === CardType.WILD_DRAW_FOUR) {
      return 'card_wild_draw4';
    }

    // 色名を小文字に変換
    const colorName = this.getColorName(color);

    // 数字カード
    if (type === CardType.NUMBER && value !== undefined) {
      return `card_${colorName}_${value}`;
    }

    // アクションカード
    switch (type) {
      case CardType.SKIP:
        return `card_${colorName}_skip`;
      case CardType.REVERSE:
        return `card_${colorName}_reverse`;
      case CardType.DRAW_TWO:
        return `card_${colorName}_draw2`;
      default:
        return 'card_back';
    }
  }

  /**
   * 色名を取得
   */
  private getColorName(color: CardColor): string {
    switch (color) {
      case CardColor.RED:
        return 'red';
      case CardColor.BLUE:
        return 'blue';
      case CardColor.YELLOW:
        return 'yellow';
      default:
        return 'red';
    }
  }

  /**
   * カードを表にする
   */
  public flipToFront(): void {
    if (!this.isFaceDown) return;

    const textureKey = this.getTextureKey();

    this.scene.tweens.add({
      targets: this,
      scaleX: 0,
      duration: 150,
      ease: 'Power2',
      onComplete: () => {
        this.cardImage.setTexture(textureKey);
        this.cardImage.setDisplaySize(
          PHASER_CONFIG.CARD_SIZE.width,
          PHASER_CONFIG.CARD_SIZE.height
        );
        this.isFaceDown = false;
        this.scene.tweens.add({
          targets: this,
          scaleX: 1,
          duration: 150,
          ease: 'Power2',
        });
      },
    });
  }

  /**
   * カードを裏にする
   */
  public flipToBack(): void {
    if (this.isFaceDown) return;

    this.scene.tweens.add({
      targets: this,
      scaleX: 0,
      duration: 150,
      ease: 'Power2',
      onComplete: () => {
        this.cardImage.setTexture('card_back');
        this.cardImage.setDisplaySize(
          PHASER_CONFIG.CARD_SIZE.width,
          PHASER_CONFIG.CARD_SIZE.height
        );
        this.isFaceDown = true;
        this.scene.tweens.add({
          targets: this,
          scaleX: 1,
          duration: 150,
          ease: 'Power2',
        });
      },
    });
  }

  /**
   * プレイ可能状態を設定
   */
  public setPlayable(playable: boolean): void {
    this.isPlayable = playable;
    this.glowEffect.setVisible(playable && !this.isFaceDown);

    if (playable && !this.isFaceDown) {
      // 光るアニメーション
      this.scene.tweens.add({
        targets: this.glowEffect,
        alpha: { from: 0.5, to: 1 },
        duration: 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else {
      this.scene.tweens.killTweensOf(this.glowEffect);
      this.glowEffect.setAlpha(1);
    }
  }

  /**
   * 選択状態を設定
   */
  public setSelected(selected: boolean): void {
    this.isSelected = selected;

    if (selected) {
      this.scene.tweens.add({
        targets: this,
        y: this.y - 20,
        scaleX: 1.15,
        scaleY: 1.15,
        duration: 150,
        ease: 'Power2',
      });
    } else {
      this.scene.tweens.add({
        targets: this,
        scaleX: 1,
        scaleY: 1,
        duration: 150,
        ease: 'Power2',
      });
    }
  }

  /**
   * カードIDを取得
   */
  public getCardId(): string {
    return this.card.id;
  }

  /**
   * カードデータを取得
   */
  public getCard(): Card {
    return this.card;
  }

  /**
   * カードデータを更新
   */
  public updateCard(card: Card, faceDown: boolean): void {
    this.card = card;
    this.isFaceDown = faceDown;

    const textureKey = faceDown ? 'card_back' : this.getTextureKey();
    this.cardImage.setTexture(textureKey);
    this.cardImage.setDisplaySize(
      PHASER_CONFIG.CARD_SIZE.width,
      PHASER_CONFIG.CARD_SIZE.height
    );
  }

  /**
   * 裏面かどうか
   */
  public isFaceDownCard(): boolean {
    return this.isFaceDown;
  }

  /**
   * 破棄時にホバープレビューを確実にクリア
   * （pointerover 中に destroy() されると pointerout が発火せず、プレビューが残るため）
   */
  public destroy(fromScene?: boolean): void {
    try {
      this.hoverPreviewLeave?.(this.card);
    } catch (e) {
      // プレビューコールバックの失敗は破棄処理を止めない
      console.warn('CardSprite destroy: hoverPreviewLeave threw', e);
    }
    this.hoverPreviewEnter = null;
    this.hoverPreviewLeave = null;
    super.destroy(fromScene);
  }
}
